import { 
  TaxBreakdownResponse, 
  TaxDetailResponse 
} from "../../model/CfdisNonIntegrated";
import {
  ProcessedTaxItem,
  ProcessedTaxData,
  ProcessedBreakdownData,
  TaxBreakdownConfig,
  TaxBreakdownType,
  TaxBreakdownContext,
  TAX_CODE_ORDER,
  TAX_STATUS
} from "./TaxBreakdownType";
import translate from "../../i18n/Translator";

export const formatTaxAmount = (amount: number, currency: string): string => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return `$ 0.00 ${currency}`;
  }
  
  return `$ ${amount.toLocaleString('es-MX', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2,
    useGrouping: true
  })} ${currency}`;
};

export const getTaxName = (tax_code: string, type: TaxBreakdownType): string => {
  const transfers_map: { [key: string]: string } = {
    '001': 'ISR',
    '002': 'IVA', 
    '003': 'IEPS',
  };
  
  const retentions_map: { [key: string]: string } = {
    '001': 'ISR',
    '002': 'IVA', 
    '003': 'IEPS',
  };
  
  const code_to_name_map = type === 'transfers' ? transfers_map : retentions_map;
  const tax_name = code_to_name_map[tax_code];
  
  if (tax_name) {
    return translate(`cfdis.cfdis_non_integrated.modals.common.tax_names.${tax_name}`) as string;
  }
  
  return translate("cfdis.cfdis_non_integrated.modals.common.tax_names.unspecified") as string;
};

export const getTaxCodeNumber = (tax_type: string): string => {
  return tax_type || '999';
};

export const getCurrencyName = (currency_code: string): string => {
  const translation_map: { [key: string]: string } = {
    'MXN': 'Pesos mexicanos',
    'USD': 'Dólar estadounidense', 
    'EUR': 'Euro',
  };
  
  return translation_map[currency_code] || currency_code;
};

export const sortTaxesByCode = (
  taxes: ProcessedTaxItem[], 
  type: TaxBreakdownType
): ProcessedTaxItem[] => {
  return [...taxes].sort((a, b) => {
    const code_a = a.tax_code;
    const code_b = b.tax_code;
    
    const order_map = TAX_CODE_ORDER[type];
    const order_a = order_map[code_a as keyof typeof order_map] || 999;
    const order_b = order_map[code_b as keyof typeof order_map] || 999;
    
    if (order_a !== order_b) {
      return order_a - order_b;
    }
    
    const rate_a = a.rate || 0;
    const rate_b = b.rate || 0;
    return rate_a - rate_b;
  });
};

export const processTaxDetail = (detail: TaxDetailResponse, type: TaxBreakdownType): ProcessedTaxItem => {
  const tax_code = detail.tax_type || '999';
  const tax_name = getTaxName(tax_code, type);
  const invoice_count = detail.invoice_count || 0;
  const rate = detail.rate;
  const has_rate = rate !== null && rate !== undefined && rate > 0;  
  const validateStatus = (status: string): 'PENDING_LOADING' | 'CANCELLATION_REQUESTED' => {
    return status === 'CANCELLATION_REQUESTED' ? 'CANCELLATION_REQUESTED' : 'PENDING_LOADING';
  };
  
  const result = {
    tax_type: tax_name,          
    tax_code: tax_code,         
    rate: rate,
    has_rate: has_rate,
    invoice_count: invoice_count,
    amount: detail.amount || 0,
    currency: detail.currency || 'MXN',
    status: validateStatus(detail.status || 'PENDING_LOADING')
  };
  
  return result;
};

export const separateTaxesByStatus = (
  tax_details: TaxDetailResponse[], 
  context: TaxBreakdownContext,
  type: TaxBreakdownType
): { pending: ProcessedTaxItem[], cancellation: ProcessedTaxItem[] } => {
  
  const pending: ProcessedTaxItem[] = [];
  const cancellation: ProcessedTaxItem[] = [];
  
  tax_details.forEach((detail, index) => {
    const processed_item = processTaxDetail(detail, type);
    const has_valid_data = processed_item.amount > 0;
    if (!has_valid_data) {
      return;
    }
    
    if (processed_item.invoice_count === 0) {
      processed_item.invoice_count = 1;
    }
    
    if (context === 'unregistered') {
      pending.push({
        ...processed_item,
        status: 'PENDING_LOADING'
      });
    } else {
      const has_status = detail.status;
      
      if (has_status === 'CANCELLATION_REQUESTED') {
        cancellation.push({
          ...processed_item,
          status: 'CANCELLATION_REQUESTED'
        });
      } else {
        pending.push({
          ...processed_item,
          status: 'PENDING_LOADING'
        });
      }
    }
  });
  
  return { pending, cancellation };
};

export const calculateTotals = (items: ProcessedTaxItem[]): number => {
  return items.reduce((total, item) => total + (item.amount || 0), 0);
};

export const processCurrencyData = (
  currency: string,
  tax_details: TaxDetailResponse[],
  type: TaxBreakdownType,
  context: TaxBreakdownContext
): ProcessedTaxData => {
  
  const { pending, cancellation } = separateTaxesByStatus(tax_details, context, type);
  
  const sorted_pending = sortTaxesByCode(pending, type);
  const sorted_cancellation = sortTaxesByCode(cancellation, type);
  
  const total_pending = calculateTotals(sorted_pending);
  const total_cancellation = calculateTotals(sorted_cancellation);
  
  return {
    currency,
    currency_name: getCurrencyName(currency),
    total_pending,
    total_cancellation,
    pending_items: sorted_pending,
    cancellation_items: sorted_cancellation,
    has_discrepancy: context !== 'unregistered' && total_pending !== total_cancellation
  };
};

export const sortCurrencies = (currencies: ProcessedTaxData[]): ProcessedTaxData[] => {
  return [...currencies].sort((a, b) => {
    if (a.currency === 'MXN' && b.currency !== 'MXN') return -1;
    if (a.currency !== 'MXN' && b.currency === 'MXN') return 1;
    return a.currency.localeCompare(b.currency);
  });
};

export const processBreakdownData = (
  data: TaxBreakdownResponse | undefined,
  type: TaxBreakdownType,
  context: TaxBreakdownContext
): ProcessedBreakdownData => {
  const currency_data = data?.by_currency;
  
  if (!currency_data || Object.keys(currency_data).length === 0) {
    return {
      currencies: [],
      is_empty: true,
      show_cancellation_section: false
    };
  }
  
  const processed_currencies: ProcessedTaxData[] = [];
  let has_any_cancellation = false;
  
  Object.entries(currency_data).forEach(([currency, tax_details]) => {
    
    if (!tax_details || tax_details === null || tax_details === undefined) {
      return;
    }
    
    let tax_details_array: TaxDetailResponse[];
    
    if (Array.isArray(tax_details)) {
      tax_details_array = tax_details;
    } else if (typeof tax_details === 'object' && tax_details !== null) {
      const values = Object.values(tax_details);
      tax_details_array = values.filter(item => 
        item && typeof item === 'object' && item !== null
      ) as TaxDetailResponse[];
    } else {
      return;
    }
    
    if (tax_details_array.length === 0) {
      return;
    }
    
    try {
      const currency_data_processed = processCurrencyData(currency, tax_details_array, type, context);
      
      if (currency_data_processed.pending_items.length > 0 || currency_data_processed.cancellation_items.length > 0) {
        processed_currencies.push(currency_data_processed);
        
        if (currency_data_processed.cancellation_items.length > 0) {
          has_any_cancellation = true;
        }
      }
    } catch (error) {
    }
  });
  
  const sorted_currencies = sortCurrencies(processed_currencies);
  
  const result = {
    currencies: sorted_currencies,
    is_empty: sorted_currencies.length === 0,
    show_cancellation_section: context !== 'unregistered' && has_any_cancellation
  };
  
  return result;
};

export const generateModalConfig = (
  type: TaxBreakdownType,
  context: TaxBreakdownContext,
  payment_method: string,
  start_date: string,
  end_date: string,
  issuer_name?: string
): TaxBreakdownConfig => {
  
  const title_key = type === 'transfers' 
    ? "cfdis.cfdis_non_integrated.modals.transfers_breakdown.title"
    : "cfdis.cfdis_non_integrated.modals.retentions_breakdown.title";
  
  const subtitle_key = type === 'transfers'
    ? "cfdis.cfdis_non_integrated.modals.transfers_breakdown.subtitle"
    : "cfdis.cfdis_non_integrated.modals.retentions_breakdown.subtitle";
  
  const empty_message_key = type === 'transfers'
    ? "cfdis.cfdis_non_integrated.modals.transfers_breakdown.empty_message"
    : "cfdis.cfdis_non_integrated.modals.retentions_breakdown.empty_message";
  
  return {
    title: translate(title_key) as string,
    subtitle: translate(subtitle_key, {
      method: payment_method,
      startDate: start_date,
      endDate: end_date
    }) as string,
    empty_message: translate(empty_message_key) as string,
    pending_section_title: '',
    cancellation_section_title: translate("cfdis.cfdis_non_integrated.modals.common.cancellation_request_section") as string
  };
};

export const formatTaxLine = (item: ProcessedTaxItem): string => {
  const tax_name = item.tax_type;
  
  const rate_text = item.has_rate && item.rate != null 
    ? ` ${(item.rate * 100).toFixed(0)}%` 
    : '';
  
  const invoice_singular = translate("cfdis.cfdis_non_integrated.modals.common.invoice_count") as string || "comprobante";
  const invoice_plural = translate("cfdis.cfdis_non_integrated.modals.common.invoice_count_plural") as string || "comprobantes";
  const invoice_text = item.invoice_count === 1 ? invoice_singular : invoice_plural;
  
  const count_text = `(${item.invoice_count.toLocaleString('es-MX')} ${invoice_text})`;
  
  return `${tax_name}${rate_text} ${count_text}`;
};

export const isValidBreakdownData = (data: TaxBreakdownResponse | undefined): boolean => {
  const currency_data = data?.by_currency;
  return !!(currency_data && Object.keys(currency_data).length > 0);
};